import Router from 'next/router';
import { type CSSProperties, memo, useCallback, useMemo, useRef } from 'react';

import { useTrackClickedPublicBoardLink } from '@air/analytics';

import { PublicTopBarAccountInfo } from '~/components/Layouts/PublicBoardLayout/PublicTopBarAccountInfo';
import { PublicBoardView } from '~/components/PublicBoard/PublicBoardView';
import { PublicBoardActivityContainer } from '~/components/PublicBoardActivityContainer';
import { PublicBoardAssetModal } from '~/components/PublicBoardAsset/PublicBoardAssetModal';
import { usePublicShareWorkspaceAccess } from '~/components/PublicShareWorkspaceAccessProvider';
import { PUBLIC_TOP_BAR_HEIGHT, PublicTopBar } from '~/components/PublicTopBar';
import { PublicWorkspaceInfo } from '~/components/PublicWorkspaceInfo';
import { PublicWorkspaceNavigation } from '~/components/PublicWorkspaceNavigation';
import { MainContent } from '~/components/Shared/ClipGallery/Content';
import { PublicHeaderBar } from '~/components/Shared/HeaderBar/PublicHeaderBar';
import { SCROLL_CONTAINER_ID } from '~/components/TableView/ui';
import { createBoardRoute } from '~/constants/routes';
import { usePublicCustomColors } from '~/hooks/usePublicCustomColors';
import { useRerenderOnMount } from '~/hooks/useRerenderOnMount';
import { useAccountContext } from '~/providers/AccountProvider';
import { AnnotationProvider } from '~/providers/AnnotationProvider/AnnotationProvider';
import { usePublicAssetModalNavigation } from '~/providers/PublicAssetModalNavigationProvider';
import { centralizedBoardSelector } from '~/store/centralizedBoard/selectors';
import { useAirStore } from '~/utils/ReduxUtils';

export const ViewableBoard = memo(() => {
  const store = useAirStore();
  const { data: account } = useAccountContext();
  const { viewerIsMemberOfPublicWorkspace } = usePublicShareWorkspaceAccess();
  const mainRef = useRef<HTMLDivElement | null>(null);
  const headerRef = useRef<HTMLDivElement | null>(null);
  const { trackClickedPublicBoardLink } = useTrackClickedPublicBoardLink();
  const { activeClipId: clipId } = usePublicAssetModalNavigation();
  const { isMounted } = useRerenderOnMount();
  const { pageBgColor } = usePublicCustomColors();

  const mainContentStyle = useMemo<CSSProperties>(
    () => ({
      height: `calc(100% - ${PUBLIC_TOP_BAR_HEIGHT}px)`,
      ...(pageBgColor ? { backgroundColor: pageBgColor } : {}),
    }),
    [pageBgColor],
  );

  const onWorkspaceNavigate = useCallback(() => {
    const centralizedBoard = centralizedBoardSelector(store.getState());

    if (!centralizedBoard) {
      return;
    }

    Router.push(createBoardRoute(centralizedBoard.id, centralizedBoard.title));
    trackClickedPublicBoardLink({
      name: 'clicked view in workspace',
      page_area: 'banner',
      page_type: 'public board',
    });
  }, [store, trackClickedPublicBoardLink]);

  const workspaceInfo = useMemo(() => {
    return viewerIsMemberOfPublicWorkspace ? (
      <PublicWorkspaceNavigation onWorkspaceNavigate={onWorkspaceNavigate} />
    ) : (
      <PublicWorkspaceInfo />
    );
  }, [onWorkspaceNavigate, viewerIsMemberOfPublicWorkspace]);

  return (
    <>
      <PublicTopBar
        workspaceInfo={workspaceInfo}
        accountInfo={account ? <PublicTopBarAccountInfo account={account} /> : null}
      />

      <MainContent
        id={SCROLL_CONTAINER_ID}
        data-testid="PUBLIC_BOARD_MOBILE_PAGE_BACKGROUND"
        ref={mainRef}
        style={mainContentStyle}
      >
        <div className="flex h-fit flex-1 flex-col">
          <PublicHeaderBar headerRef={headerRef} />
          <div className="grow" data-testid="PUBLIC_BOARD_CHILDREN_CONTAINER">
            <PublicBoardView headerRef={headerRef} scrollElementRef={mainRef} />
            {/*
              We check here if isMounted is true or not because clipId is always false on the server but is there
              on first render client side and this check prevents a rehydration issue
            */}
            {!!clipId && isMounted && (
              <AnnotationProvider>
                <PublicBoardAssetModal />
              </AnnotationProvider>
            )}
          </div>
        </div>
        <PublicBoardActivityContainer />
      </MainContent>
    </>
  );
});

ViewableBoard.displayName = 'ViewableBoard';
